/**
 * 🎟️ PROMO CODE SERVICE
 * Admin API service for managing discount codes
 */

import { getApiBaseUrl } from '@/config/environment';


// ========================================
// TYPES
// ========================================

export type CodeType = 'seasonal' | 'event' | 'partner' | 'first-rider' | 'generic';
export type DiscountType = 'percentage' | 'fixed';

export interface PromoCodeEligibility {
  newUsersOnly?: boolean;
  minBookingAmount?: number;
  maxBookingAmount?: number;
  locations?: string[];
  serviceTypes?: string[];
  specificUsers?: string[];
}

export interface PromoCode {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: CodeType;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountAmount?: number;

  eligibility?: PromoCodeEligibility;

  maxRedemptions?: number;
  maxPerUser?: number;
  currentRedemptions: number;

  validFrom: string;
  validUntil: string;

  isActive: boolean;

  partnerName?: string;
  campaignId?: string;

  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PromoCodeStats {
  overview: {
    totalCodes: number;
    activeCodes: number;
    inactiveCodes: number;
    totalRedemptions: number;
  };
  discounts: {
    totalDiscountGiven: number;
    averageDiscount: number;
    currency: string;
  };
  byType: Array<{
    type: CodeType;
    count: number;
    redemptions: number;
  }>;
  topPerforming: Array<{
    id: string;
    code: string;
    name: string;
    redemptions: number;
    totalDiscount: number;
  }>;
  expiringSoon: Array<{
    id: string;
    code: string;
    name: string;
    validUntil: string;
    daysRemaining: number;
  }>;
}

export interface CodeUsage {
  id: string;
  code: string;
  userId: string;
  userName: string;
  userEmail: string;
  bookingId: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  location?: string;
  serviceType?: string;
  usedAt: string;
}

// ========================================
// SERVICE CLASS
// ========================================

class PromoCodeService {
  private baseUrl = `${getApiBaseUrl()}/api/admin/promo-codes`;

  private getAuthHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * 🎬 Initialize database tables
   * GET /api/admin/promo-codes/init-database
   */
  async initDatabase(): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/init-database`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize database (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error initializing database:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initialize database'
      };
    }
  }

  /**
   * 📋 Get all promo codes
   * GET /api/admin/promo-codes
   */
  async getCodes(filters?: {
    type?: CodeType | 'all';
    isActive?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: {
      codes: PromoCode[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));

      const url = `${this.baseUrl}${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch promo codes (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching promo codes:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch promo codes'
      };
    }
  }

  /**
   * 🔍 Get code details
   * GET /api/admin/promo-codes/:id
   */
  async getCodeById(codeId: string): Promise<{
    success: boolean;
    data?: PromoCode;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/${codeId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch code details (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching code details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch code details'
      };
    }
  }

  /**
   * ➕ Create promo code
   * POST /api/admin/promo-codes
   */
  async createCode(codeData: Partial<PromoCode>): Promise<{
    success: boolean;
    data?: PromoCode;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(codeData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to create promo code (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error creating promo code:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create promo code'
      };
    }
  }

  /**
   * ✏️ Update promo code
   * PUT /api/admin/promo-codes/:id
   */
  async updateCode(codeId: string, updates: Partial<PromoCode>): Promise<{
    success: boolean;
    data?: PromoCode;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/${codeId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update promo code (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error updating promo code:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update promo code'
      };
    }
  }

  /**
   * 🗑️ Delete promo code
   * DELETE /api/admin/promo-codes/:id
   */
  async deleteCode(codeId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/${codeId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to delete promo code (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error deleting promo code:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete promo code'
      };
    }
  }

  /**
   * 📊 Get statistics
   * GET /api/admin/promo-codes/stats
   */
  async getStats(): Promise<{
    success: boolean;
    data?: PromoCodeStats;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/stats`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch statistics (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching statistics:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch statistics'
      };
    }
  }

  /**
   * 📋 Get usage history
   * GET /api/admin/promo-codes/:id/usage
   */
  async getCodeUsage(codeId: string, filters?: {
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: {
      usage: CodeUsage[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));

      const url = `${this.baseUrl}/${codeId}/usage${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch code usage (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching code usage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch code usage'
      };
    }
  }

  /**
   * 🎲 Bulk generate codes
   * POST /api/admin/promo-codes/bulk-generate
   */
  async bulkGenerateCodes(config: {
    quantity: number;
    prefix: string;
    name: string;
    discountType: DiscountType;
    discountValue: number;
    validUntil: string;
    maxRedemptions?: number;
    maxPerUser?: number;
    type?: CodeType;
  }): Promise<{
    success: boolean;
    data?: {
      codes: PromoCode[];
      count: number;
    };
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/bulk-generate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate codes (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error generating bulk codes:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate bulk codes'
      };
    }
  }
}

export const promoCodeService = new PromoCodeService();
export default promoCodeService;
